import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import styles from "./styles.module.scss";
import CoachInfo from "../AllCoachesPublicSession/CoachInfo";
import { DateTime } from "luxon";
import { AvailableSlotModel } from "app/models/scheduling/AvailableSlotModel";
import { useTranslation } from "react-i18next";
import { setBookSession } from "app/actions/scheduling/bookSession";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import SessionInputForm from "./_components/SessionInputForm";
import { Loading } from "app/components/Wrappers";
import { cls } from "app/utils";
import earth_icon from "app/images/earth.svg";
import { getSingleCoachPublicTimeSlots, mapDays } from "../helpers";
import AuthenticateModal from "app/routes/PublicCalendar/AuthenticateModal";
import { RootState } from "typedefs";
import { useHistory } from "react-router-dom";

type SlotParams = {
  coach_profile_ids: string[];
  from: string;
  to?: string;
  slot_duration: number;
  lesson_rate_id?: number;
  ignore_lesson_id?: number | null;
};

interface IRightPanel {
  bookSession: any;
  lessonRates: any;
  lessonRate: any;
  handleLessonRateChange: (value: number) => void;
  timezone: string;
}

const RightPanel = ({
  bookSession,
  lessonRate,
  lessonRates,
  handleLessonRateChange,
  timezone,
}: IRightPanel) => {
  const coach = bookSession?.coach;
  const today = DateTime.now().setZone(coach?.timeZone).startOf("day");
  const selectedDay = DateTime.fromISO(bookSession?.date, {
    zone: coach?.timeZone,
  });
  const [slotsGroupedByDate, setSlots] = useState<
    Record<string, AvailableSlotModel[]>
  >({});
  const { t } = useTranslation();
  const [endDate, setEndDate] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const selectedDayRef = useRef<HTMLDivElement | null>(null);
  const [previousDate, setPreviousDate] = useState<DateTime | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const user = useSelector((state: RootState) => state.user, shallowEqual);
  // Authenticated visitors (e.g. a student who just signed up mid-booking and
  // were redirected back here) must fetch slots from the authenticated endpoint,
  // which passes their student_profile_id so GP capacity / already-booked
  // filtering matches booking-time validation. Anonymous visitors keep using
  // the public (contextless) endpoint.
  const isPublicPage = user.data === null;
  // Identifier mismatch between the two endpoints: the public available_times
  // controller looks coaches up by calendar_uuid, the authenticated (v2) one by
  // external_id. Send whichever the target endpoint expects.
  const coachAvailabilityId = isPublicPage ? coach?.calendarUuid : coach?.externalId;
  const history = useHistory();
  const prevBookSessionDateRef = useRef<string | null>(null);

  // Check if booking limit is reached (remaining_bookings === 0 means limit reached, null means unlimited)
  // Note: API returns snake_case, so we check both formats for compatibility
  const remainingBookings = bookSession?.pricingValue?.remainingBookings ?? bookSession?.pricingValue?.remaining_bookings;
  const isBookingLimitReached = remainingBookings === 0;

  const handleSlotClick = (slot) => {
    dispatch(
      setBookSession({
        date: slot.startHour.startOf("day").toISODate(),
        time: {
          startHour: slot.startHour.toMillis(),
          endHour: slot.endHour.toMillis(),
          timeZone: slot.startHour.get("zone").zoneName,
          coachId: slot.coachProfileId,
        },
        coach,
        selectedSession: slot,
        duration: slot.duration,
      }),
    );

    if (user.data === null) {
      // Scroll to top before opening modal
      window.scrollTo(0, 0);
      document.body.scrollTop = 0;

      setIsModalOpen(true);

      // Additional scroll after a brief delay for Safari
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 50);
    } else {
      history.push("/public/confirmation");
    }
  };

  const renderSlots = (slots: AvailableSlotModel[]) => {
    if (slots.length === 0) {
      return (
        <div className={styles.no_slots}>
          {t("No available timeslots on this day.")}
        </div>
      );
    }

    return slots.map((slot) => (
      <div
        className={styles.slot}
        key={`${slot.timeId}-${slot.coachProfileId}`}
        onClick={() => handleSlotClick(slot)}
      >
        {`${slot.startHour.toFormat("h:mm a")}`}
      </div>
    ));
  };

  const scrollToSelectedDate = (
    prevDate: DateTime | null,
    newDate: DateTime,
    offsetPx: number = 80,
  ) => {
    const el = selectedDayRef.current;
    const container = el?.parentElement;
    if (!el || !container) {
      return;
    }

    el.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });

    setTimeout(() => {
      const isScrollingBackward = prevDate && prevDate > newDate;

      container.scrollBy({
        top: isScrollingBackward ? -offsetPx : offsetPx,
        behavior: "smooth",
      });

      const tryScroll = (retries = 4) => {
        const el = selectedDayRef.current;
        const container = el?.parentElement;
        if (!el || !container) {
          return;
        }

        const containerRect = container.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();

        const nowVisible =
          elRect.top >= containerRect.top &&
          elRect.bottom <= containerRect.bottom;

        if (!nowVisible && retries > 0) {
          setTimeout(() => tryScroll(retries - 1), 80);
          return;
        }

        el.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      };

      setTimeout(() => {
        tryScroll();
      }, 250);
    }, 200);
  };

  useEffect(() => {
    if (!bookSession.date || prevBookSessionDateRef.current === bookSession.date) {
      return undefined;
    }

    const container = containerRef.current;
    if (!container) {
      return undefined;
    }

    const scrollToElement = (): void => {
      const element = selectedDayRef.current;

      if (!element) {
        return;
      }

      const offset = element.offsetTop - container.offsetTop;
      container.scrollTop = offset;
    };

    const observer = new MutationObserver(() => {
      if (selectedDayRef.current) {
        scrollToElement();
        observer.disconnect();
      }
    });

    observer.observe(container, { childList: true, subtree: true });

    scrollToElement();

    prevBookSessionDateRef.current = bookSession.date;

    return () => observer.disconnect();
  }, [bookSession.date]);


  useEffect(() => {
    // Skip fetching timeslots if booking limit is reached
    if (isBookingLimitReached) {
      setSlots({});
      setLoading(false);
      return;
    }

    let from: DateTime;

    if (selectedDay.diff(today).as("days") <= 7) {
      from = today;
    } else {
      from = selectedDay.minus({ days: 7 }).startOf("day");
    }

    const to = selectedDay.plus({ days: 7 }).endOf("day");

    setEndDate(to);
    setStartDate(from);

    const params: SlotParams = {
      slot_duration: bookSession?.pricingValue?.durationInMinutes,
      lesson_rate_id: bookSession?.pricingValue?.id,
      from: from.toISO(),
      to: to.toISO(),
      coach_profile_ids: [coachAvailabilityId],
    };

    const initialAccumulator = mapDays(from, to);

    setLoading(true);

    getSingleCoachPublicTimeSlots(params, initialAccumulator, isPublicPage)
      .then((slots: Record<string, AvailableSlotModel[]>) => {
        setSlots(slots);
        setLoading(false);
      })
      .finally(() => {
        if (user.data !== null) {
          scrollToSelectedDate(previousDate, bookSession?.date);
        }
        setPreviousDate(bookSession?.date);
      });
  }, [bookSession, isBookingLimitReached]);

  const loadMore = () => {
    if (loading) {
      return;
    }

    const newFrom = endDate.plus({ days: 1 }).startOf("day");
    const newTo = newFrom.plus({ days: 2 }).endOf("day");

    setEndDate(newTo);

    const newSlotsGroupedByDate = mapDays(newFrom, newTo);
    const initialAccumulator = {
      ...slotsGroupedByDate,
      ...newSlotsGroupedByDate,
    };

    const params: SlotParams = {
      slot_duration: bookSession?.pricingValue?.durationInMinutes,
      from: newFrom.toISO(),
      to: newTo.toISO(),
      coach_profile_ids: [coachAvailabilityId],
      lesson_rate_id: bookSession?.pricingValue?.id,
    };

    setLoading(true);

    getSingleCoachPublicTimeSlots(params, initialAccumulator, isPublicPage).then(
      (slots: Record<string, AvailableSlotModel[]>) => {
        setSlots(slots);
        setLoading(false);
      },
    );
  };

  const loadPrevious = () => {
    if (loading) {
      return;
    }

    let newTo: DateTime;

    if (startDate.diff(today).as("days") === 0) {
      return;
    } else if (startDate.diff(today).as("days") <= 1) {
      newTo = today;
    } else {
      newTo = startDate.minus({ days: 1 }).endOf("day");
    }

    const newFrom = newTo.minus({ days: 2 }).startOf("day");

    setStartDate(newFrom);

    const newSlotsGroupedByDate = mapDays(newFrom, newTo);
    const initialAccumulator = {
      ...slotsGroupedByDate,
      ...newSlotsGroupedByDate,
    };

    const params: SlotParams = {
      slot_duration: bookSession?.pricingValue?.durationInMinutes,
      from: newFrom.toISO(),
      to: newTo.toISO(),
      coach_profile_ids: [coachAvailabilityId],
      lesson_rate_id: bookSession?.pricingValue?.id,
    };

    setLoading(true);

    getSingleCoachPublicTimeSlots(params, initialAccumulator, isPublicPage).then(
      (slots: Record<string, AvailableSlotModel[]>) => {
        setSlots(slots);
        setLoading(false);
      },
    );
  };

  const handleScroll = () => {
    const container = containerRef.current;

    if (!container || loading) {
      return;
    }

    if (container.scrollTop / container.scrollHeight < 0.15) {
      loadPrevious();
    }

    if ((container.scrollTop + container.clientHeight) / container.scrollHeight > 0.9) {
      loadMore();
    }
  };

  const debounce = (func: (..._args: any[]) => void, wait: number) => {
    let timeout: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  const debouncedHandleScroll = useMemo(
    () => debounce(handleScroll, 100),
    [handleScroll]
  );

  useEffect(() => {
    if (!containerRef.current || loading) {
      return;
    }

    const container = containerRef.current;
    const hasScrollbar = container.scrollHeight > container.clientHeight;

    if (!hasScrollbar) {
      const tryFetch = async () => {
        loadMore();

        setTimeout(() => {
          const stillNoScroll = container.scrollHeight <= container.clientHeight;
          if (stillNoScroll && !loading) {
            tryFetch();
          }
        }, 300);
      };

      tryFetch();
    }
  }, [slotsGroupedByDate, loading]);

  const renderDurationValue = () => {
    if (bookSession?.pricingValue) {
      // CN-2711: the option keeps its price to itself — duration alone.
      if (bookSession.pricingValue.hidePrice) {
        return `${bookSession.pricingValue.duration}`;
      }

      return `${bookSession?.pricingValue?.duration} • ${coach?.currencySymbol}${bookSession?.pricingValue?.rate}`;
    }

    return "";
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  return (
    <div className={styles.right_panel}>
      <div className={styles.header}>
        <div className={styles.timezone_container}>
          <img src={earth_icon} className={styles.earth_ico} />
          <span>{timezone}</span>
        </div>
        <div className={styles.session}>
          <div>
            <div className={styles.title}>
              {bookSession?.pricingValue?.title || ""}
            </div>
            <div className={styles.duration_value}>{renderDurationValue()}</div>
          </div>
          <SessionInputForm
            lessonRates={lessonRates}
            lessonRate={lessonRate}
            handleLessonRateChange={handleLessonRateChange}
            isMobile={true}
          />
        </div>
        <CoachInfo coach={coach} singleCoach={true} />
      </div>
      <div
        className={styles.days_container}
        ref={containerRef}
        onScroll={debouncedHandleScroll}
      >
        {isBookingLimitReached ? (
          <div className={styles.booking_limit_reached}>
            <div className={styles.limit_icon}>
              <i className="ico ico-warning" />
            </div>
            <div className={styles.limit_message}>
              {t("Booking limit reached")}
            </div>
            <div className={styles.limit_details}>
              {t("You have reached the maximum number of future bookings for this option.")}
            </div>
            <div className={styles.limit_hint}>
              {t("Please wait for an existing booking to complete before booking again.")}
            </div>
          </div>
        ) : (
          <>
            {slotsGroupedByDate &&
              Object.entries(slotsGroupedByDate)
                .sort(
                  (dateA, dateB) =>
                    new Date(dateA[0]).getTime() - new Date(dateB[0]).getTime(),
                )
                .map(([date, slots]) => {
                  const formattedDate = DateTime.fromISO(date);
                  const year = formattedDate.toFormat("yyyy");
                  const currentYear = DateTime.now().toFormat("yyyy");
                  const monthDay = formattedDate.toFormat("MMM d");
                  const dayName = formattedDate.toFormat("cccc").toUpperCase();

                  return (
                    <div
                      className={cls(styles.day_container, {
                        [styles.today]: date === selectedDay.toISODate(),
                      })}
                      key={`${date}`}
                      ref={date === selectedDay.toISODate() ? selectedDayRef : null}
                    >
                      <div
                        className={cls(styles.date, {
                          [styles.selected_date]: date === today.toISODate(),
                        })}
                      >
                        {year !== currentYear && (
                          <div className={styles.year}>{year}</div>
                        )}
                        <div className={styles.month_day}>{monthDay}</div>
                        <div className={styles.day_name}>{dayName}</div>
                      </div>
                      <div className={styles.slots_container}>
                        {renderSlots(slots)}
                      </div>
                    </div>
                  );
                })}
            {loading && <Loading isLoading loadType="spinner" />}
          </>
        )}
      </div>
      <AuthenticateModal
        isOpen={isModalOpen}
        onClose={closeModal}
        flow='booking'
        urlValues={{ coachName: coach.fullName, coachId: coach.externalId }}
        headerValues={{ date: DateTime.fromISO(bookSession.date) }}
      />
    </div>
  );
};

export default memo(RightPanel);
